import React, { useContext, useRef } from 'react';
import { Handle, NodeProps, Position } from 'reactflow';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import BotResponseTreeNode from '../../../../types/BotBuilder/BotResponseTreeNode';
import MessageBlockTreeNode from '../../../../types/BotBuilder/MessageBlockTreeNode';
import MessageBlockContent from '../../../../types/BotBuilder/MessageBlockContent';
import ResponseBlock from './ResponseBlock/ResponseBlock';
import PreviewModeContext from '../../../../context/PreviewModeContext/PreviewModeContext';
import BotBuilderNode from '../BotBuilderNode/BotBuilderNode';
import BotBuilderContext from '../../../../context/BotBuilderContext/BotBuilderContext';

export type ResponseNodeData = {
  node: BotResponseTreeNode;
  onNodeUpdate: (node: BotResponseTreeNode) => void;
  onNodeDelete: (node: BotResponseTreeNode) => void;
  intentCategory: string;
  onHighlight?: (nodeId: string) => void;
  isLocked?: boolean;
};

function ResponseNode(props: NodeProps<ResponseNodeData>): React.ReactElement {
  const { data, isConnectable } = props;
  const { onNodeDelete, onNodeUpdate } = useContext(BotBuilderContext);

  const { setIsPreviewMode } = useContext(PreviewModeContext);
  const deleteButtonRef = useRef<HTMLButtonElement>(null);
  const [editingBlockId, setEditingBlockId] = React.useState<string | null>(
    null
  );

  const isMessageBlockTreeNode = (
    content: any
  ): content is MessageBlockTreeNode => {
    return content instanceof MessageBlockTreeNode;
  };

  const messageNode = isMessageBlockTreeNode(data.node.content)
    ? data.node.content
    : null;

  const handleNodeAdd = (newNode: BotResponseTreeNode): void => {
    if (messageNode) {
      messageNode.nextStep = newNode;
      onNodeUpdate(data.node);
    }
  };

  const deleteMessageBlock = async (): Promise<void> => {
    onNodeDelete(data.node);
  };

  const handleBlockEditStart = (blockId: string): void => {
    if (data.isLocked) return;
    setEditingBlockId(blockId);
    setIsPreviewMode(false);
    data.onHighlight?.(data.node.id);
  };

  const handleBlockEditEnd = (): void => {
    setEditingBlockId(null);
  };

  const renderMessageBlocks = () => {
    if (!messageNode) {
      return null;
    }

    const combinedContent =
      messageNode.messageBlocks
        .map((block) => block.content || '')
        .filter((content) => content.trim() !== '')
        .join('\n\n') || null;

    const virtualBlock: MessageBlockContent = {
      id: 'combined',
      content: combinedContent,
      contentType: 'MARKDOWN',
      blockOrder: 0
    };

    return (
      <ResponseBlock
        key='combined'
        index={0}
        id='combined'
        step={virtualBlock}
        messageNode={messageNode}
        data={data}
        onNodeUpdate={onNodeUpdate}
        isEditing={editingBlockId === 'combined'}
        onEditStart={() => handleBlockEditStart('combined')}
        onEditEnd={handleBlockEditEnd}
        isLocked={data.isLocked}
      />
    );
  };

  return (
    <BotBuilderNode
      title='Your assistant says:'
      deleteButtonRef={deleteButtonRef}
      onNodeDelete={deleteMessageBlock}
      sourceNode={data.node}
      onNodeAdd={handleNodeAdd}
      showAddButton={!messageNode?.nextStep}
      category={data.intentCategory}
      isLocked={data.isLocked}
    >
      <div className='flex items-start gap-3 pb-4 pr-4'>
        <div
          className='w-10 h-10 flex items-center justify-center rounded-full p-2 shrink-0'
          style={{ backgroundColor: '#049A8F' }}
        >
          <SmartToyIcon className='w-5 h-5' style={{ color: '#002E5F' }} />
        </div>
        <div className='flex flex-col gap-2 flex-1 min-w-0'>
          {renderMessageBlocks()}
        </div>
      </div>
      <div className='-mb-2'>
        <Handle
          type='target'
          position={Position.Top}
          style={{ top: -10 }}
          isConnectable={isConnectable}
          id='a'
        />
        <Handle
          type='source'
          position={Position.Bottom}
          id='b'
          style={{ bottom: -10 }}
          isConnectable={isConnectable}
        />
      </div>
    </BotBuilderNode>
  );
}

export default ResponseNode;
